/**
 * Clinic-time arithmetic for the booking funnel (FR-BOOK-05, FR-BOOK-06).
 *
 * The clinic works in Pacific. Tebra, the schedule store and the availability API all
 * speak Pacific wall-clock time; only the DISPLAY is converted to the patient's own
 * timezone, and the note explaining that is shown only when the two differ. A patient in
 * New York who reads "10:00 AM" and assumes Eastern is three hours out, which is the
 * whole reason this exists.
 *
 * Ported, behaviour for behaviour, from the live page script captured in
 * `apps/patient/parity/book/inline-scripts.js`. The double application of the offset in
 * `clinicWallToUTC` is deliberate: it is what makes the two DST transition days resolve
 * correctly, and it is in the live code for the same reason.
 */

/** The clinic's timezone. Every slot time the platform stores is this zone's wall clock. */
export const CLINIC_TZ = 'America/Los_Angeles';

/** `BK_CONFIG.BOOKING_WINDOW_MIN_DAYS` — the first bookable day is two days out. */
export const BOOKING_WINDOW_MIN_DAYS = 2;

/** `BK_CONFIG.BOOKING_WINDOW_MAX_DAYS`. */
export const BOOKING_WINDOW_MAX_DAYS = 30;

/** `BK_CONFIG.OPEN_WEEKDAYS` — Monday to Friday (0 = Sunday). */
export const OPEN_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MON = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * The one place this module reads the wall clock, so a test can pass an explicit instant
 * to every function below instead of freezing global time.
 * TODO(M5.7): take the injected Clock (TESTING.md §2.1) once the booking flow has a
 * server round trip to hang one off; until then the window is computed in the browser.
 */
function nowMs(): number {
  return new Date().getTime();
}

/** The visitor's own timezone, or the clinic's if the browser will not say. */
export function patientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || CLINIC_TZ;
  } catch {
    return CLINIC_TZ;
  }
}

/** How far `tz` is from UTC at `utcMs`, in milliseconds. DST-aware. */
export function tzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));

  const bag: Record<string, string> = {};
  for (const part of parts) bag[part.type] = part.value;

  const asUTC = Date.UTC(
    Number(bag['year']),
    Number(bag['month']) - 1,
    Number(bag['day']),
    bag['hour'] === '24' ? 0 : Number(bag['hour']),
    Number(bag['minute']),
    Number(bag['second']),
  );
  return asUTC - utcMs;
}

/**
 * Clinic wall clock (`"2026-09-17"`, `"10:00"`) to the real UTC instant.
 * The offset is applied twice so a slot on a DST transition day resolves to the instant
 * a person would actually turn up at.
 */
export function clinicWallToUTC(dateStr: string, timeStr: string): number {
  const [y = 0, m = 1, d = 1] = dateStr.split('-').map(Number);
  const [hh = 0, mm = 0] = timeStr.split(':').map(Number);
  const naive = Date.UTC(y, m - 1, d, hh, mm);
  const once = naive - tzOffsetMs(naive, CLINIC_TZ);
  return naive - tzOffsetMs(once, CLINIC_TZ);
}

/** `"3:00 PM"` in `tz`. */
export function fmtTimeInTZ(utcMs: number, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(utcMs));
}

/** `"PDT"` / `"EDT"` in `tz` at that instant, or `""` if the browser will not say. */
export function tzShortName(utcMs: number, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
      hour: 'numeric',
    }).formatToParts(new Date(utcMs));
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** `"Thu, Sep 17"` in `tz`. */
export function fmtDateInTZ(utcMs: number, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(utcMs));
}

/**
 * Is the patient already on clinic time? Offsets are compared, not zone names:
 * America/Vancouver and America/Los_Angeles are the same clock, and a visitor there
 * should not be told their times have been converted.
 */
export function patientOnClinicTime(patientTz: string, now: number = nowMs()): boolean {
  return tzOffsetMs(now, patientTz) === tzOffsetMs(now, CLINIC_TZ);
}

export interface BookableDate {
  /** `"2026-09-17"` — the clinic-local date, which is the key the API is asked for. */
  iso: string;
  /** `"Thu"` */
  weekday: string;
  /** `"17"` */
  day: string;
  /** `"Sep"` */
  month: string;
  /** `"Thursday, September 17, 2026"` — the chip's accessible name. */
  accessibleLabel: string;
}

function isoDate(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * The date-chip window: every open weekday from `BOOKING_WINDOW_MIN_DAYS` to
 * `BOOKING_WINDOW_MAX_DAYS` days ahead, computed against the visitor's own midnight —
 * exactly as the live `bookableDates()` does.
 *
 * TODO(M5.7): the window rules and which days actually have a provider belong to the
 * platform's availability endpoint (ADR-009, FR-BOOK-05). Computing them in the browser
 * is a parity behaviour, not a design; replace this with `GET /booking/dates`.
 */
export function bookableDates(from: Date = new Date()): BookableDate[] {
  const today = new Date(from);
  today.setHours(0, 0, 0, 0);

  const out: BookableDate[] = [];
  for (let i = BOOKING_WINDOW_MIN_DAYS; i <= BOOKING_WINDOW_MAX_DAYS; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    if (!OPEN_WEEKDAYS.includes(d.getDay())) continue;
    out.push({
      iso: isoDate(d),
      weekday: DOW[d.getDay()] ?? '',
      day: String(d.getDate()),
      month: MON[d.getMonth()] ?? '',
      accessibleLabel: new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(d),
    });
  }
  return out;
}

/**
 * The live demo schedule: 08:30–16:00 with a 12:00–13:00 lunch, Monday to Thursday, in
 * 30-minute slots. The live page uses it only when its availability endpoint is blank.
 *
 * TODO(M5.7): delete. Slots come from `GET /booking/slots` once M5.1 lands; until then
 * this keeps the grid, its empty states and the timezone conversion demonstrable.
 */
const DEMO_SCHEDULE: Record<
  number,
  { start: string; end: string; lunch: [string, string] } | null
> = {
  0: null,
  1: { start: '08:30', end: '16:00', lunch: ['12:00', '13:00'] },
  2: { start: '08:30', end: '16:00', lunch: ['12:00', '13:00'] },
  3: { start: '08:30', end: '16:00', lunch: ['12:00', '13:00'] },
  4: { start: '08:30', end: '16:00', lunch: ['12:00', '13:00'] },
  5: null,
  6: null,
};

const SLOT_MINUTES = 30;

function timeToMin(t: string): number {
  const [h = 0, m = 0] = t.split(':').map(Number);
  return h * 60 + m;
}

function minToTime(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Clinic-local `"HH:MM"` slot starts for a date, from the demo schedule. */
export function demoSlotsForDate(dateStr: string): string[] {
  const d = new Date(`${dateStr}T00:00:00`);
  const sched = DEMO_SCHEDULE[d.getDay()];
  if (!sched) return [];

  const slots: string[] = [];
  const startM = timeToMin(sched.start);
  const endM = timeToMin(sched.end);
  const lunchS = timeToMin(sched.lunch[0]);
  const lunchE = timeToMin(sched.lunch[1]);

  for (let m = startM; m + SLOT_MINUTES <= endM; m += SLOT_MINUTES) {
    if (m >= lunchS && m < lunchE) continue;
    slots.push(minToTime(m));
  }
  return slots;
}

/**
 * The clinic-local wall clock of a real instant: `"10:00"`, or `"2026-09-17"`.
 *
 * The inverse of `clinicWallToUTC`, and the reason the funnel can carry an instant from
 * `GET /public/availability/slots` and still render the live page's `HH:MM` grid. Used
 * when a saved draft is hydrated, where the instant is all that was stored.
 */
export function utcToClinicWall(
  instant: string | number | Date,
  tz: string = CLINIC_TZ,
  part: 'time' | 'date' = 'time',
): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(instant));

  const bag: Record<string, string> = {};
  for (const piece of parts) bag[piece.type] = piece.value;

  if (part === 'date') return `${bag['year'] ?? ''}-${bag['month'] ?? ''}-${bag['day'] ?? ''}`;
  const hour = bag['hour'] === '24' ? '00' : (bag['hour'] ?? '00');
  return `${hour}:${bag['minute'] ?? '00'}`;
}

/**
 * The chips for a list of clinic-local dates the availability endpoint returned.
 *
 * `bookableDates()` computes the window in the browser, which is a parity behaviour, not
 * a design (FR-BOOK-05 puts the window on the server). This builds the same chip out of
 * a date the server named, so the strip's markup and its accessible labels are identical
 * whichever of the two produced it.
 */
export function describeDate(iso: string): BookableDate {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  /* Noon local, so no timezone rounding can move the label to the day before. */
  const at = new Date(y, m - 1, d, 12);
  return {
    iso,
    weekday: DOW[at.getDay()] ?? '',
    day: String(at.getDate()),
    month: MON[at.getMonth()] ?? '',
    accessibleLabel: new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(at),
  };
}

/**
 * The date-chip window for a tenant's booking window (FR-BOOK-05, API.md §2.3).
 *
 * The same rule `bookableDates()` implements, with the two numbers taken from
 * `GET /public/tenant` instead of from the live page's `BK_CONFIG`. It is what the strip
 * shows BEFORE a state is chosen - `GET /public/availability/dates` needs a state, and
 * the live page has its chips in the first frame - and it is replaced by the server's
 * answer the moment there is one.
 */
export function bookableDatesInWindow(
  minLeadDays: number,
  maxHorizonDays: number,
  from: Date = new Date(),
): BookableDate[] {
  const today = new Date(from);
  today.setHours(0, 0, 0, 0);

  const out: BookableDate[] = [];
  for (let i = minLeadDays; i <= maxHorizonDays; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    if (!OPEN_WEEKDAYS.includes(d.getDay())) continue;
    out.push({
      iso: isoDate(d),
      weekday: DOW[d.getDay()] ?? '',
      day: String(d.getDate()),
      month: MON[d.getMonth()] ?? '',
      accessibleLabel: new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(d),
    });
  }
  return out;
}

/** `"2026-09-17"` for a `Date`, in the browser's own reckoning. */
export function toLocalIsoDate(d: Date): string {
  return isoDate(d);
}
